/**
 * The predicate filter format used by the Engage user sync, ADO 186742
 * "Engage-Inform User Synchronization and Merged User Creation", item 10.
 *
 * Replaces the raw Neo4j GraphQL `where` fragment the two filter fields held before.
 * A filter is a JSON array of predicates, all of which must hold. There is no OR, no
 * nesting, and no expression language — the whole grammar is below, deliberately, so
 * there is nothing to maintain on either side.
 *
 *   [
 *     { "property": "businessCountry", "operator": "in", "value": ["US", "CA"] },
 *     { "property": "jobTitle",        "operator": "eq", "value": "Manager" },
 *     { "property": "jobStartDate",    "operator": "exists" }
 *   ]
 *
 * Validation here is a courtesy to the admin, not a security boundary: the integration
 * service checks property names against its own Cypher identifier validator before
 * interpolating them, and always passes values as Cypher parameters. What this buys is
 * that a filter which would be rejected upstream — or, worse, silently drop rows on a
 * cleanup read — cannot be saved in the first place.
 */

/** Operators the integration service implements. */
export const INFORM_FILTER_OPERATORS = Object.freeze([
  "eq",
  "ne",
  "in",
  "notIn",
  "gt",
  "gte",
  "lt",
  "lte",
  "exists",
  "notExists",
]);

/** Operators that test presence and therefore carry no value. */
const VALUELESS_OPERATORS = Object.freeze(["exists", "notExists"]);

/** Operators whose value must be an array. */
const LIST_OPERATORS = Object.freeze(["in", "notIn"]);

/**
 * Property names must be Cypher identifiers. Leading underscores are allowed and
 * expected — the merged node's own properties are `_isSyncable`, `_mergedAt`,
 * `_nodeId`, `_clientNodeId`.
 */
const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * What to say about an empty array, per run. The sync filter is required for a feed-backed
 * tenant, so blank is no way out there; the cleanup filter is optional, and blank turns it off.
 */
const EMPTY_ARRAY_MESSAGES = Object.freeze({
  sync: "An empty sync filter is not allowed. Add at least one predicate.",
  cleanup:
    "An empty cleanup filter is not allowed. To turn the cleanup off, leave the field blank. Otherwise, add at least one predicate.",
});

/** A worked example, shown under both fields so the shape is never guessed at. */
export const INFORM_FILTER_EXAMPLE = `[
  { "property": "businessCountry", "operator": "eq", "value": "US" }
]`;

/**
 * Validate one predicate.
 * @param {*} predicate
 * @param {number} index Position in the array, for a message that points at the problem.
 * @returns {string|null} An error message, or null when the predicate is well formed.
 */
const validatePredicate = (predicate, index) => {
  const at = `Filter entry ${index + 1}`;

  if (predicate === null || typeof predicate !== "object" || Array.isArray(predicate)) {
    return `${at} must be an object.`;
  }

  const { property, operator } = predicate;

  if (typeof property !== "string" || !property) {
    return `${at} is missing a "property".`;
  }
  if (!IDENTIFIER.test(property)) {
    return `${at} has an invalid property name "${property}". Use letters, digits and underscores, starting with a letter or underscore.`;
  }
  if (typeof operator !== "string" || !operator) {
    return `${at} is missing an "operator".`;
  }
  if (!INFORM_FILTER_OPERATORS.includes(operator)) {
    return `${at} uses an unknown operator "${operator}". Supported: ${INFORM_FILTER_OPERATORS.join(
      ", "
    )}.`;
  }

  const hasValue = Object.prototype.hasOwnProperty.call(predicate, "value");

  if (VALUELESS_OPERATORS.includes(operator)) {
    if (hasValue) {
      return `${at} uses "${operator}", which tests presence and takes no "value".`;
    }
    return null;
  }

  if (!hasValue) {
    return `${at} uses "${operator}" and needs a "value".`;
  }

  if (LIST_OPERATORS.includes(operator)) {
    if (!Array.isArray(predicate.value)) {
      return `${at} uses "${operator}", so "value" must be an array.`;
    }
    if (predicate.value.length === 0) {
      // An empty list matches nothing, which on a cleanup read means deleting nobody
      // and on a sync read means syncing nobody. Either way it is not what was meant.
      return `${at} uses "${operator}" with an empty list, which would match no users.`;
    }
    return null;
  }

  if (
    Array.isArray(predicate.value) ||
    (predicate.value !== null && typeof predicate.value === "object")
  ) {
    return `${at} uses "${operator}", so "value" must be a single value.`;
  }

  return null;
};

/**
 * Validate a filter as typed into one of the two filter fields.
 *
 * An empty value is valid — both fields are optional at this level, and the sync's own
 * requirement that it have a filter is enforced in `InformSettings.vue` (`syncFilterError`)
 * rather than here.
 *
 * An empty array is not, ADO 192402. `[]` used to match every user with a feed record, and
 * the worker now skips the run for it, so the screen refuses it rather than store a value
 * that reads as "everyone" and does nothing.
 *
 * @param {string} value Raw field contents.
 * @param {"sync"|"cleanup"} [runName] The run the filter is for, which picks the empty-array
 *   message. Without it, an empty array still fails, with a generic message.
 * @returns {string|null} An error message, or null when the filter is well formed.
 */
export const validateInformFilter = (value, runName) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return null;

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (error) {
    return `Not valid JSON: ${error.message}`;
  }

  if (!Array.isArray(parsed)) {
    return "A filter must be an array of predicates.";
  }

  if (parsed.length === 0) {
    return EMPTY_ARRAY_MESSAGES[runName] ?? "A filter must have at least one predicate.";
  }

  for (let index = 0; index < parsed.length; index += 1) {
    const error = validatePredicate(parsed[index], index);
    if (error) return error;
  }

  return null;
};

/**
 * Whether a saved value is in the predicate format.
 *
 * Used on load to tell an admin that what is stored is the old GraphQL fragment and
 * needs rewriting, rather than showing them a field that looks merely wrong. Every
 * filter saved before this change falls here, which is the intent: the format changed
 * under them and silence would be the worse outcome.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isInformFilterFormat = (value) => validateInformFilter(value) === null;
